import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { branchService } from '@/data/branchService';
import { Branch } from '@/types/Branch.interface';
import { BranchTaxIdentity } from '@/enums/branch-tax-identity';
import { hasTaxSetupSupport, taxSetupErrorMessage } from '@/utils/b2bTaxErrors';
import { useUserViewMode } from '@/hooks/useUserViewMode';

interface BranchTaxIdentityCardProps {
  branch: Branch;
  onSaved: (branch: Branch) => void;
}

/**
 * Whose tax number the branch invoices under in B2B: the company's, or its
 * own (entered in the financial information). Between branches of one
 * taxpayer there is no invoice and no VAT, so this decides how a delivery
 * is settled. Shown only when the backend returns `taxIdentity`; only the
 * system admin and the company admin change it (the backend answers 403
 * `B2B_TAX_IDENTITY_FORBIDDEN` to anyone else).
 */
const BranchTaxIdentityCard = ({
  branch,
  onSaved
}: BranchTaxIdentityCardProps) => {
  const { t } = useTranslation();
  const { isSuperAdmin, isCompanyAdmin } = useUserViewMode();
  const canEdit = isSuperAdmin || isCompanyAdmin;

  const [value, setValue] = useState<BranchTaxIdentity | ''>(
    branch.taxIdentity ?? ''
  );
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  useEffect(() => {
    setValue(branch.taxIdentity ?? '');
  }, [branch.id, branch.taxIdentity]);

  if (!hasTaxSetupSupport(branch, 'taxIdentity')) return null;

  const handleSave = async () => {
    if (!value) return;
    setFeedback(null);
    try {
      setSaving(true);
      const formData = new FormData();
      formData.append('taxIdentity', value);
      await branchService.update(branch.id, formData);
      onSaved({ ...branch, taxIdentity: value });
      setFeedback({ severity: 'success', message: t('branch.tax.saved') });
    } catch (error: any) {
      setFeedback({
        severity: 'error',
        message: taxSetupErrorMessage(error, t, t('branch.tax.saveError'))
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom>
          {t('branch.tax.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('branch.tax.description')}
        </Typography>

        {!branch.taxIdentity && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('branch.tax.notSet')}
          </Alert>
        )}
        {value === BranchTaxIdentity.OWN && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('branch.tax.ownNeedsFinancialInfo')}
          </Alert>
        )}
        {feedback && (
          <Alert severity={feedback.severity} sx={{ mb: 2 }}>
            {feedback.message}
          </Alert>
        )}

        <RadioGroup
          value={value}
          onChange={(e) => setValue(e.target.value as BranchTaxIdentity)}
        >
          <FormControlLabel
            value={BranchTaxIdentity.COMPANY}
            control={<Radio />}
            label={t('branch.tax.company')}
            disabled={!canEdit || saving}
          />
          <FormControlLabel
            value={BranchTaxIdentity.OWN}
            control={<Radio />}
            label={t('branch.tax.own')}
            disabled={!canEdit || saving}
          />
        </RadioGroup>

        {canEdit ? (
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={
                saving || !value || value === (branch.taxIdentity ?? '')
              }
              startIcon={saving ? <CircularProgress size={18} /> : undefined}
            >
              {t('save')}
            </Button>
          </Box>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {t('branch.tax.readOnly')}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default BranchTaxIdentityCard;
